import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { AuthState } from '../types';

/**
 * Determines whether the current session is:
 *   - owner  (magic-link auth'd user, auth.uid() used by RLS)
 *   - viewer (anonymous session with `viewer_for` JWT claim, read-only)
 *   - none   (no session)
 */
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    role: 'none',
    email: null,
    ownerIdForViewer: null,
    loading: true,
  });

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!active) return;

      if (!session) {
        setState({ role: 'none', email: null, ownerIdForViewer: null, loading: false });
        return;
      }

      // Decode the JWT payload (base64url, middle segment) to check for viewer_for claim
      const payload = decodeJwtPayload(session.access_token);
      const viewerFor = payload?.viewer_for as string | undefined;

      if (viewerFor) {
        setState({
          role: 'viewer',
          email: null,
          ownerIdForViewer: viewerFor,
          loading: false,
        });
      } else {
        setState({
          role: 'owner',
          email: session.user.email ?? null,
          ownerIdForViewer: null,
          loading: false,
        });
      }
    };

    resolve();

    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      resolve();
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const [, payload] = jwt.split('.');
    const normalised = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalised + '='.repeat((4 - (normalised.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
}
